import { useLazyQuery, useMutation } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { MUTATION_START_UPDATE_BY_ID, QUERY_UPDATE_BY_ID } from '../../../../graphql/update';

export const useUpdateItem = (setUpdatesList, data) => {
  const [startUpdate] = useMutation(MUTATION_START_UPDATE_BY_ID);
  const [updatingStatus] = useLazyQuery(QUERY_UPDATE_BY_ID);

  // ======================================================
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const progressTimer = useRef(null);
  const [downloadState, setDownloadState] = useState({});

  const isInstalledRef = useRef(downloadState.isInstalled);

  // ======================================================

  const startDownloading = async (id) => {
    try {
      const { data: downloadData } = await startUpdate({ variables: { id } });
      
      return downloadData.updates.download === 'Initiated Downloading process';
    } catch (error) {
      enqueueSnackbar(error?.graphQLErrors?.[0]?.message || t('An unexpected error has occurred'), {
        variant: 'error',
      });
      setDownloadState((prev) => ({
        ...prev,
        isPreparing: false,
      }));
      return false;
    }
  };

  const refetchTimer = (id) => {
    progressTimer.current = setInterval(async () => {
      // Check if installation is complete or not
      if (isInstalledRef.current) {
        clearInterval(progressTimer.current);
        progressTimer.current = null;
        return;
      }

      await refetchDownloadState(id);
    }, 5000);
  };

  const refetchDownloadState = async (id) => {
    try {
      const { data: updatingData } = await updatingStatus({ variables: { id } });
      const { DownloadProgress, IsDownloading, IsInstalled } = updatingData.updates.byId;

      setDownloadState({
        isPreparing: false,
        downloadProgress: DownloadProgress,
        isDownloading: IsDownloading,
        isInstalled: IsInstalled,
      });

      // if (DownloadProgress === 100 && !IsDownloading && IsInstalled) {
      //   setUpdatesList((prev) => {
      //     const updatesMap = new Map(prev.map((item) => [item.Id, item]));
      //     updatesMap.set(id, { ...data, isNew: false });
      //     return Array.from(updatesMap.values());
      //   });
      // }

    } catch (error) {
      enqueueSnackbar(error?.graphQLErrors?.[0]?.message || t('An unexpected error has occurred'), {
        variant: 'error',
      });
    }
  };

  const handleDownload = async (id) => {
    setDownloadState((prev) => ({
      ...prev,
      isPreparing: true,
    }));
    // Download begin
    const status = await startDownloading(id);
    // Refetching download state
    if (status) refetchTimer(id);
  };

  useEffect(() => {
    isInstalledRef.current = downloadState.isInstalled;
  }, [downloadState]);

  useEffect(() => {
    setDownloadState({
      isPreparing: false,
      downloadProgress: data.DownloadProgress,
      isDownloading: data.IsDownloading,
      isInstalled: data.IsInstalled,
    });

    if (data.IsDownloading) {
      refetchTimer(data.Id);
    }
  }, [data]);

  // Cleanup interval on component unmount
  useEffect(() => () => progressTimer.current && clearInterval(progressTimer.current), []);

  return { handleDownload, downloadState };
};
